import React from 'react'
import { useLocation } from '@/utils/url'
import { scrollToElement } from '@/utils/dom'
import usePrevious from '@/utils/usePrevious'
import { SearchProvider } from '@/components/search-provider'
import NinetailedProviderWrapper from '@/components/ninetailed-provider'
import { mapNtContentfulAudiences, mapNtContentfulExperiences } from '@/utils/ninetailed'

const { VITE_NINETAILED_CLIENT_ID, VITE_NINETAILED_CLIENT_ENV } = import.meta.env

type BaseLayoutProps = {
  page: React.ReactNode & {
    props: any
  }
}

export default function BaseLayout({ page }: BaseLayoutProps) {
  const { url, hash, path } = useLocation()
  const prevPath = usePrevious(path)
  const isSamePage = prevPath === path
  const { ntAudiences, ntExperiences } = page.props

  // Handle page scroll on navigation
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    const t = setTimeout(() => {
      const documentScrollPosition = history.state?.documentScrollPosition
      const scrollToTop = () => {
        window.scroll({ top: 0, behavior: 'instant' })
      }
      const scrollToScrollPosition = () => {
        window.scroll({ top: documentScrollPosition.top, behavior: 'instant' })
      }
      const scrollToHashPosition = () => {
        const element = document.getElementById(hash)
        if (element) {
          scrollToElement(element, 0, { behavior: 'instant' })
        } else {
          scrollToTop()
        }
      }

      if (isSamePage) {
        // if navigating within the same page, prefer scroll to hash first, then scroll position
        if (hash) {
          scrollToHashPosition()
        } else if (documentScrollPosition?.top !== undefined) {
          scrollToScrollPosition()
        }
      } else {
        // if navigating to a new page, prefer scroll to scroll position first, then hash
        if (documentScrollPosition?.top !== undefined) {
          scrollToScrollPosition()
        } else if (hash) {
          scrollToHashPosition()
        } else {
          scrollToTop()
        }
      }
    }, 0)
    return () => clearTimeout(t)
  }, [url, hash, isSamePage])

  return (
    <NinetailedProviderWrapper
      allExperiences={mapNtContentfulExperiences(ntExperiences)}
      allAudiences={mapNtContentfulAudiences(ntAudiences)}
      ninetailedClientId={VITE_NINETAILED_CLIENT_ID}
      ninetailedEnv={VITE_NINETAILED_CLIENT_ENV}
    >
      <SearchProvider>{page}</SearchProvider>
    </NinetailedProviderWrapper>
  )
}
